import { useState, useEffect, useMemo } from 'react';
import { apiGet } from '../api/client';
import { useSortableTable } from '../hooks/useSortableTable';
import { useSlowRequestWarning } from '../hooks/useSlowRequestWarning';
import Toast from './Toast';
import UnifiedTreeView from './UnifiedTreeView';

function VocabularyTable({ config }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [toast, setToast] = useState({ show: false, message: '' });
  const [viewMode, setViewMode] = useState('table');
  
  const showSlowWarning = useSlowRequestWarning(loading, 2000);

  const fetchData = async () => {
    setLoading(true);
    try {
      const json = await apiGet(config.endpoint);
      setData(json);
    } catch (error) {
      setToast({ show: true, message: '❌ Error loading data: ' + error.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [config.endpoint]);

  const filteredData = useMemo(() => {
    if (!searchTerm) return data;
    return data.filter(item =>
      Object.entries(item).some(([key, val]) => {
        if (Array.isArray(val)) {
          return val.some(v => String(v).toLowerCase().includes(searchTerm.toLowerCase()));
        }
        if (key === 'children' && val && typeof val === 'object') {
          return Object.entries(val).some(([childKey, childData]) => {
            const childDesc = childData?.description || '';
            return childKey.toLowerCase().includes(searchTerm.toLowerCase()) ||
                   childDesc.toLowerCase().includes(searchTerm.toLowerCase());
          });
        }
        return String(val).toLowerCase().includes(searchTerm.toLowerCase());
      })
    );
  }, [data, searchTerm]);

  const { sortedData, requestSort, sortConfig } = useSortableTable(filteredData);

  const getSortIcon = (columnKey) => {
    if (sortConfig?.key !== columnKey) return '';
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };

  const copyColumn = (columnKey) => {
    const values = sortedData.map(item => {
      const val = item[columnKey];
      if (Array.isArray(val)) {
        return val.join(', ');
      }
      if (columnKey === 'children' && val && typeof val === 'object') {
        return Object.keys(val).join(', ');
      }
      return val;
    }).join('\n');
    
    navigator.clipboard.writeText(values).then(() => {
      setToast({ show: true, message: '📋 Column copied to clipboard!' });
    });
  };

  const flattenAssetClasses = (dataArray) => {
    const result = [];
    dataArray.forEach(item => {
      result.push({
        id: item.id,
        path: item.display_name || item.id,
        description: item.description || '',
        level: 0
      });
      if (item.children && Object.keys(item.children).length > 0) {
        Object.entries(item.children).forEach(([childId, childData]) => {
          result.push({
            id: childId,
            path: `${item.display_name || item.id} → ${childId}`,
            description: childData.description || '',
            level: 1
          });
        });
      }
    });
    return result;
  };

  const copyAllNodes = () => {
    const allNodes = config.hasTreeView ? flattenAssetClasses(data).map(n => n.id) : data.map(item => item.id || item.value);
    navigator.clipboard.writeText(allNodes.join('\n')).then(() => {
      setToast({ show: true, message: '📋 All nodes copied to clipboard!' });
    });
  };

  if (loading) {
    return (
      <div className="spinner">
        <div>⏳ Loading...</div>
        {showSlowWarning && (
          <div style={{
            marginTop: '15px',
            padding: '12px 20px',
            background: '#fef3c7',
            border: '1px solid #fbbf24',
            borderRadius: '6px',
            color: '#92400e',
            fontSize: '13px',
            maxWidth: '500px',
            margin: '15px auto 0'
          }}>
            ⚠️ Backend may be waking up from sleep (cold start). This can take up to 20 seconds on first request.
          </div>
        )}
      </div>
    );
  }

  if (sortedData.length === 0 && searchTerm) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon">🔍</div>
        <p>No results found for "{searchTerm}"</p>
      </div>
    );
  }

  return (
    <div>
      <div className="controls">
        <div className="controls-left">
          <span className="badge">{sortedData.length} {sortedData.length === 1 ? 'item' : 'items'}</span>
          <button onClick={fetchData}>
            🔄 Refresh
          </button>
          {config.hasTreeView && (
            <button onClick={copyAllNodes}>
              📋 Copy All Nodes
            </button>
          )}
        </div>
        <div style={{display: 'flex', gap: '10px', alignItems: 'center'}}>
          {config.hasTreeView && (
            <div className="view-toggle">
              <button 
                className={viewMode === 'tree' ? 'active' : ''} 
                onClick={() => setViewMode('tree')}
              >
                🌳 Tree View
              </button>
              <button 
                className={viewMode === 'table' ? 'active' : ''} 
                onClick={() => setViewMode('table')}
              >
                📋 Flat List
              </button>
            </div>
          )}
          <input
            type="text"
            placeholder="🔍 Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{width: config.hasTreeView ? '220px' : '280px'}}
          />
        </div>
      </div>

      {viewMode === 'tree' && config.hasTreeView ? (
        <div className="table-wrapper" style={{padding: '20px'}}>
          <UnifiedTreeView 
            data={data} 
            searchTerm={searchTerm}
            config={{
              getNodeId: (id) => id,
              getNodeData: (item) => item,
              getDescription: (item) => item.description || '',
              getMetadata: (item) => (
                <>
                  {item.display_name && (
                    <span className="badge-small badge-purple">{item.display_name}</span>
                  )}
                  {item.borrower_category && (
                    <span className="badge-small badge-blue">{item.borrower_category}</span>
                  )}
                  {item.collateral_type && (
                    <span className="badge-small badge-green">{item.collateral_type}</span>
                  )}
                  {item.aka && item.aka.length > 0 && item.aka.slice(0, 2).map((aka, i) => (
                    <span key={i} className="badge-small badge-gray">{aka}</span>
                  ))}
                </>
              ),
              getChildren: (item) => item.children || null
            }}
          />
        </div>
      ) : (
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                {config.columns.map(col => (
                  <th
                    key={col.key}
                    style={{ width: col.width }}
                    onClick={() => col.sortable !== false && requestSort(col.key)}
                  >
                    {col.label}{col.sortable !== false && getSortIcon(col.key)}
                    {col.sortable !== false && (
                      <button
                        className="copy-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          copyColumn(col.key);
                        }}
                      >
                        📋
                      </button>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedData.map((item, idx) => (
                <tr key={idx}>
                  {config.columns.map(col => (
                    <td key={col.key}>
                      {config.renderCell ? config.renderCell(item, col) : item[col.key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Toast show={toast.show} message={toast.message} onClose={() => setToast({ show: false, message: '' })} />
    </div>
  );
}

export default VocabularyTable;
